import React from "react";
import { motion } from "framer-motion";
import CtaLink from "./CtaLink";
import "./PageIntro.css";

/**
 * The opening statement shared by the Home, Services and About Us pages:
 * the page heading, the brand rule, an optional sub-heading, the introduction
 * split into standalone cards, and an optional CTA.
 *
 * Content in, presentation only out — this component never supplies copy.
 *
 * @param {string}   id          id for the heading, for the section's aria-labelledby
 * @param {string}   title       the heading text
 * @param {string}   [titleAs]   heading level, defaults to h1
 * @param {string}   [subtitle]  sub-heading; rendered one level below the title
 * @param {string[]} paragraphs  the introduction, one string per card
 * @param {string[]} [images]    illustrations, positional against `paragraphs`
 * @param {object}   [cta]       `{ to, label }` for the closing button
 * @param {boolean}  [rule]      the orange rule under the heading (default on)
 */
const PageIntro = ({
  id,
  title,
  titleAs: TitleTag = "h1",
  subtitle,
  subtitleAs: SubtitleTag = "h2",
  paragraphs = [],
  images = [],
  cta,
  rule = true,
}) => (
  <section className="page-intro py-14 lg:py-20" aria-labelledby={id}>
    <div className="sectionlayout">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.55, ease: "easeOut" }}
        className="mx-auto max-w-6xl text-center"
      >
        <TitleTag
          id={id}
          className="mx-auto max-w-4xl text-3xl md:text-4xl lg:text-[2.5rem] font-extrabold leading-tight tracking-tight text-[var(--brand-primary)]"
        >
          {title}
        </TitleTag>
        {rule && (
          <div className="mx-auto mt-4 h-1 w-40 rounded-full bg-[var(--brand-orange)]" />
        )}
        {subtitle && (
          <SubtitleTag className="mx-auto mt-6 max-w-3xl text-lg md:text-xl font-bold text-gray-900">
            {subtitle}
          </SubtitleTag>
        )}
        {paragraphs.length > 0 && (
          <div
            className={`mt-10 grid grid-cols-1 gap-6 ${
              paragraphs.length >= 3 ? "lg:grid-cols-3" : "lg:grid-cols-2"
            }`}
          >
            {paragraphs.map((paragraph, i) => (
              <div key={paragraph} className="page-intro__point">
                {images[i] && (
                  /* decorative: the paragraph beside it carries the meaning */
                  <span className="page-intro__art" aria-hidden="true">
                    <img
                      className="page-intro__img"
                      src={images[i]}
                      alt=""
                      loading="lazy"
                    />
                  </span>
                )}
                <p className="text-sm md:text-base leading-relaxed text-gray-600">
                  {paragraph}
                </p>
              </div>
            ))}
          </div>
        )}
        {cta && (
          <div className="mt-9">
            <CtaLink to={cta.to} label={cta.label} />
          </div>
        )}
      </motion.div>
    </div>
  </section>
);

export default PageIntro;
